import { memo, useCallback } from 'react';

import { Close } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { Portal } from '@air/primitive-portal';

import { TryCanvasPopoverVideos } from '~/components/TryCanvasPopover/TryCanvasPopoverVideos';
import { useIsUserFromOnboardingPrompt } from '~/components/v2/store/features/onboarding/useOnboardingPreferencesStore';
import { useDraftCanvas } from '~/hooks/canvases/useDraftCanvas';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export type TryCanvasPopoverProps = {
  boardId?: string;
  onClose: () => void;
};

type ModalCopy = {
  title: string;
  description: string;
  buttonText: string;
  dismissButtonText: string;
};

const FIRST_USER_COPY: ModalCopy = {
  title: 'Welcome! Start by creating a Canvas',
  description: "Canvas is where you'll edit and scale your assets. Get started with a blank canvas.",
  buttonText: 'Create a Canvas',
  dismissButtonText: 'No thanks',
};

const EXISTING_USER_COPY: ModalCopy = {
  title: 'Introducing: Canvas',
  description:
    'A brand new way to edit and scale your assets — all in one place. Turn one approved asset into hundreds of on-brand deliverables.',
  buttonText: 'Try Canvas',
  dismissButtonText: 'Maybe later',
};

export const TryCanvasPopover = memo(({ boardId, onClose }: TryCanvasPopoverProps) => {
  const { openDraftCanvas } = useDraftCanvas();
  const { currentWorkspace } = useCurrentWorkspace();

  const isFirstTimeUser = useIsUserFromOnboardingPrompt(currentWorkspace?.id);
  const modalCopy = isFirstTimeUser ? FIRST_USER_COPY : EXISTING_USER_COPY;

  const handleCheckItOutClick = useCallback(() => {
    openDraftCanvas({
      boardId,
      analyticsContext: { source: 'try_canvas_cta', board_id: boardId },
    });
    onClose();
  }, [boardId, openDraftCanvas, onClose]);

  return (
    <Portal>
      <div
        className="fixed right-8 bottom-16 z-10 w-[386px] overflow-hidden rounded-8 bg-gray-1 shadow-popover"
        data-testid="TRY_CANVAS_POPOVER"
      >
        <div className="relative aspect-[360/202] rounded-t-8">
          <TryCanvasPopoverVideos />
          <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-16 bg-gradient-to-t from-black/10 to-transparent" />
          <IconButton
            className="absolute top-3 right-3"
            size="small"
            appearance="ghost"
            color="grey"
            icon={Close}
            label="Close"
            onClick={onClose}
          />
        </div>
        <div className="flex flex-col gap-4 px-8 pt-4 pb-6">
          <p className="font-semibold text-24 text-gray-12">{modalCopy.title}</p>
          <p className="text-14 text-gray-11">{modalCopy.description}</p>
          <div className="flex justify-end gap-2">
            <Button
              appearance="ghost"
              color="grey"
              data-testid="TRY_CANVAS_POPOVER_DISMISS_BUTTON"
              onClick={onClose}
              size="large"
            >
              {modalCopy.dismissButtonText}
            </Button>
            <Button
              appearance="filled"
              color="blue"
              data-testid="TRY_CANVAS_POPOVER_CHECK_IT_OUT_BUTTON"
              size="large"
              onClick={handleCheckItOutClick}
            >
              {modalCopy.buttonText}
            </Button>
          </div>
        </div>
      </div>
    </Portal>
  );
});

TryCanvasPopover.displayName = 'TryCanvasPopover';
